// Recipe photos that couldn't upload yet, kept on this device until they can. A photo leaves the queue only once
// the server has it (or refuses it for good), so a dropped signal never loses an original.
import { useSyncExternalStore } from 'react'
import { ApiError } from './api'
import type { Crop } from '../../shared/crop'
import type { PreparedPhoto } from './photos'
import { uploadPhoto, type PhotoRole } from './recipes'

// Photos are kept as plain bytes: Safari refuses to put a Blob or File in IndexedDB in a private window.
type Bytes = { data: ArrayBuffer; type: string }
type Queued = {
  key: string
  recipeId: string
  addedAt: number
  name: string
  original: Bytes
  display: Bytes
  thumb: Bytes
  width: number
  height: number
  // Left out by photos queued before cropping, and by photos not cropped.
  crop?: Crop | null
  // Left out by photos queued before photos had kinds: those were all of the card.
  kind?: PhotoRole['kind']
  hero?: boolean
}

const bytes = async (blob: Blob): Promise<Bytes> => ({ data: await blob.arrayBuffer(), type: blob.type })
const blob = (b: Bytes) => new Blob([b.data], { type: b.type })

export async function toQueued(recipeId: string, photo: PreparedPhoto, role?: PhotoRole): Promise<Queued> {
  return {
    ...role,
    key: crypto.randomUUID(),
    recipeId,
    addedAt: Date.now(),
    name: photo.original.name,
    original: await bytes(photo.original),
    display: await bytes(photo.display),
    thumb: await bytes(photo.thumb),
    width: photo.width,
    height: photo.height,
    crop: photo.crop ?? null,
  }
}

export const toPhoto = (item: Queued): PreparedPhoto => ({
  original: new File([item.original.data], item.name, { type: item.original.type }),
  display: blob(item.display),
  thumb: blob(item.thumb),
  width: item.width,
  height: item.height,
  crop: item.crop ?? null,
})

const DB = 'gatheredfrom-uploads'
const STORE = 'photos'

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'key' })
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open()
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode)
      const req = work(tx.objectStore(STORE))
      tx.oncomplete = () => resolve(req.result)
      tx.onerror = () => reject(tx.error)
    })
  } finally {
    db.close()
  }
}

// Counts per recipe, for the "waiting to upload" note; listeners hear about every change.
let counts: Record<string, number> = {}
const listeners = new Set<() => void>()
async function refresh() {
  const all = await run('readonly', (s) => s.getAll() as IDBRequest<Queued[]>).catch(() => [])
  counts = {}
  for (const item of all) counts[item.recipeId] = (counts[item.recipeId] ?? 0) + 1
  listeners.forEach((fn) => fn())
}

export function usePendingUploads(recipeId: string) {
  return useSyncExternalStore(
    (fn) => {
      if (listeners.size === 0) void refresh()
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
    () => counts[recipeId] ?? 0,
  )
}

export async function enqueue(recipeId: string, photos: { prepared: PreparedPhoto; role: PhotoRole }[]) {
  // Ask the browser not to clear this site's storage while photos are waiting in it.
  void navigator.storage?.persist?.().catch(() => false)
  for (const photo of photos) {
    const item = await toQueued(recipeId, photo.prepared, photo.role)
    await run('readwrite', (s) => s.put(item))
  }
  await refresh()
}

// A failed fetch (no signal, dropped connection), a signed-out session or a busy server is worth retrying; a
// refusal of the photo itself isn't.
export const worthRetrying = (e: unknown) =>
  e instanceof TypeError || (e instanceof ApiError && (e.status === 401 || e.status === 429 || e.status >= 500))

let draining: Promise<void> | null = null

// Uploads whatever is waiting, oldest first. Safe to call often: only one run happens at a time.
export function drainUploads(onUploaded: (recipeId: string) => void) {
  draining ??= (async () => {
    try {
      const all = await run('readonly', (s) => s.getAll() as IDBRequest<Queued[]>)
      for (const item of all.sort((a, b) => a.addedAt - b.addedAt)) {
        try {
          await uploadPhoto(item.recipeId, toPhoto(item), { kind: item.kind ?? 'card', hero: item.hero })
        } catch (e) {
          if (worthRetrying(e)) break
          console.warn('a queued photo was refused', e)
        }
        await run('readwrite', (s) => s.delete(item.key))
        onUploaded(item.recipeId)
      }
    } catch {
      // Storage unavailable (private browsing); nothing was queued.
    } finally {
      draining = null
      await refresh()
    }
  })()
  return draining
}
